/* eslint-disable no-console */
import React, { useCallback, useState } from "react";
import { Container, Grid, Typography } from "@material-ui/core";
import {
	DataGridPro,
	GridColumns,
	GridToolbarColumnsButton,
	GridToolbarDensitySelector,
	GridToolbarExport,
	GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import PageBreadcrumbs from "../components/Breadcrumbs/PageBreadcrumbs";
import { breadcrumbNode } from "../components/Breadcrumbs/PageBreadcrumbs.d";
import { useBatchUpdateInventory } from "../api/replenishmentApi";
import { Box, Button, Tooltip, Snackbar, Alert } from "@mui/material";
import { DownloadOutlined, FileUploadOutlined } from "@mui/icons-material";
import useInitialAppData from "../Hook/UseInitialAppData";
import LoadingIndicator from "../components/LoadingIndicator/LoadingIndicator";

interface InventoryUpdateRow {
	id: string;
	sku: string;
	locationId: number;
	minQty: number | null;
	maxQty: number | null;
}

type SnackbarState = { open: boolean; message: string; severity: "success" | "error"; autoHide: boolean } | null;

const InventoryUpdates: React.FC = () => {
	const breadcrumbNodes: breadcrumbNode[] = [{ name: "Update Min / Max" }];

	// Reuse the displayRequired limits intentionally — the BFF's
	// /Inventory/Batch endpoint enforces a single shared limit today
	// (BHS-544 settings are scoped to `displayRequired`; the ticket notes the
	// naming "is intentionally structured to support other upload types in
	// future if needed"). If/when min-max uploads need their own limits,
	// surface a new `uploadLimits.inventory` from the BFF and switch here.
	//
	// Defensive fallback (mirrors DisplayRequired.tsx): guard against a stale
	// BFF that omits uploadLimits, so the problem surfaces as an obvious
	// console error rather than silently allowing imports of any size.
	const initialAppData = useInitialAppData();
	const limits = initialAppData.uploadLimits?.displayRequired;
	if (!limits) {
		// eslint-disable-next-line no-console
		console.error(
			"InitialAppData response is missing uploadLimits.displayRequired. " +
				"The BFF may be running an older build, or local.settings.json may not have been picked up (restart the function host after changing it). " +
				"Falling back to defaults (batchLimit=1000, importCSVLimit=10000)."
		);
	}
	const batchLimit = limits?.batchLimit ?? 1000;
	const importCSVLimit = limits?.importCSVLimit ?? 10000;

	const [rows, setRows] = useState<InventoryUpdateRow[]>([]);
	// Info banner shown above the grid when the imported CSV will be split
	// across multiple batches on the BFF.
	const [batchInfoMessage, setBatchInfoMessage] = useState<string | null>(null);
	const [snackbar, setSnackbar] = useState<SnackbarState>(null);
	const [{ loading: isSaving }, batchUpdateInventory] = useBatchUpdateInventory();

	const columns: GridColumns = [
		{ field: "sku", headerName: "SKU", width: 150, editable: false },
		{ field: "locationId", headerName: "Location ID", type: "number", width: 130, editable: false },
		{ field: "minQty", headerName: "Min", type: "number", width: 100, editable: true },
		{ field: "maxQty", headerName: "Max", type: "number", width: 100, editable: true },
	];

	const handleDownloadTemplate = useCallback(() => {
		const csvContent = "Sku,LocationId,Min,Max\n";
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", "inventory_template.csv");
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}, []);

	const handleImport = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (file) {
				const reader = new FileReader();
				reader.onload = (e) => {
					const text = e.target?.result as string;
					const lines = text.split("\n");
					const headers = lines[0]
						.toLowerCase()
						.split(",")
						.map((h) => h.trim());

					// Validate headers
					const expectedHeaders = ["sku", "locationid", "min", "max"];
					const isValidHeader = expectedHeaders.every((header) => headers.includes(header));

					if (!isValidHeader) {
						setSnackbar({
							open: true,
							message: "Invalid CSV format. Expected headers: Sku,LocationId,Min,Max",
							severity: "error",
							autoHide: true,
						});
						return;
					}

					const newRows: InventoryUpdateRow[] = [];
					for (let i = 1; i < lines.length; i++) {
						const line = lines[i].trim();
						if (line) {
							const values = line.split(",").map((v) => v.trim());
							if (values.length >= 4) {
								const skuValue = values[0];
								const locationIdValue = values[1];
								const locationIdNumber = Number(locationIdValue);
								const minNumber = values[2] === "" ? null : Number(values[2]);
								const maxNumber = values[3] === "" ? null : Number(values[3]);

								if (!skuValue) {
									setSnackbar({
										open: true,
										message: `Invalid SKU value at row ${i + 1}. Must not be blank.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}

								if (!Number.isInteger(locationIdNumber) || locationIdNumber <= 0) {
									setSnackbar({
										open: true,
										message: `Invalid LocationId value at row ${i + 1}. Must be a positive number.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}

								if (minNumber !== null && !Number.isInteger(minNumber)) {
									setSnackbar({
										open: true,
										message: `Invalid Min value at row ${i + 1}. Must be a whole number or blank.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}

								if (maxNumber !== null && !Number.isInteger(maxNumber)) {
									setSnackbar({
										open: true,
										message: `Invalid Max value at row ${i + 1}. Must be a whole number or blank.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}

								const row: InventoryUpdateRow = {
									id: `${skuValue}-${locationIdValue}`,
									sku: skuValue,
									locationId: locationIdNumber,
									minQty: minNumber,
									maxQty: maxNumber,
								};
								newRows.push(row);
							}
						}
					}

					// Row count limits (BHS-544 parity with DisplayRequired). Block
					// hard at importCSVLimit; show an info banner when the import
					// will be split into batches.
					if (newRows.length > importCSVLimit) {
						setRows([]);
						setBatchInfoMessage(null);
						setSnackbar({
							open: true,
							message: `Imports must not exceed ${importCSVLimit}`,
							severity: "error",
							autoHide: true,
						});
						return;
					}

					if (newRows.length > batchLimit) {
						setBatchInfoMessage(`Uploads will be saved in batches of ${batchLimit}.`);
					} else {
						setBatchInfoMessage(null);
					}

					setRows(newRows);
				};
				reader.readAsText(file);
			}
			// Reset the input value so the same file can be uploaded again
			event.target.value = "";
		},
		[batchLimit, importCSVLimit]
	);

	const handleSave = useCallback(async () => {
		const updateRequests = rows.map((row) => ({
			sku: row.sku,
			locationId: row.locationId,
			minQty: row.minQty,
			maxQty: row.maxQty,
		}));

		try {
			const response = await batchUpdateInventory(updateRequests);
			const successful = response.data?.successfulBatches ?? [];
			const failed = response.data?.failedBatches ?? [];

			if (failed.length === 0) {
				setSnackbar({
					open: true,
					message: "Inventory updates saved successfully!",
					severity: "success",
					autoHide: true,
				});
				return;
			}

			// At least one batch failed — keep the message visible until the
			// user dismisses it so they can read which batches need
			// re-uploading (parity with DisplayRequired, BHS-544 AC6/AC7).
			const failedList = failed.join(", ");
			const successList = successful.join(", ");
			const message =
				successful.length === 0
					? `Save failed. All ${failed.length} batch(es) were rejected (batches ${failedList}). Please retry the upload.`
					: `Saved batches ${successList}; failed batches ${failedList}. Please retry the upload.`;

			setSnackbar({
				open: true,
				message,
				severity: "error",
				autoHide: false,
			});
		} catch (error: any) {
			console.log(`${error?.message} | ${error?.response?.data}`);
			setSnackbar({
				open: true,
				message: error?.message
					? `Error saving inventory updates: ${error.message} | ${error?.response?.data}`
					: "Error saving inventory updates. Please try again.",
				severity: "error",
				autoHide: false,
			});
		}
	}, [rows, batchUpdateInventory]);

	const handleRowEdit = useCallback((updatedRow: InventoryUpdateRow) => {
		setRows((prevRows) => prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
		return updatedRow;
	}, []);

	const CustomToolbar = () => {
		return (
			<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<GridToolbarColumnsButton />
					<GridToolbarFilterButton />
					<GridToolbarDensitySelector />
					<GridToolbarExport />
					<Tooltip title="Download Import Template">
						<Button size="small" startIcon={<DownloadOutlined />} onClick={handleDownloadTemplate} sx={{ height: 36 }}>
							Template
						</Button>
					</Tooltip>
					<input
						id="inventory-import-input"
						type="file"
						accept=".csv"
						style={{ display: "none" }}
						onChange={handleImport}
					/>
					<label htmlFor="inventory-import-input">
						<Button size="small" startIcon={<FileUploadOutlined />} component="span" sx={{ height: 36 }}>
							Import
						</Button>
					</label>
					{rows.length > 0 && (
						<Button color="primary" onClick={() => handleSave()} disabled={isSaving} size="small" sx={{ height: 36 }}>
							Save All
						</Button>
					)}
				</Box>
			</Box>
		);
	};

	return (
		<Container maxWidth={false}>
			<Grid container spacing={6}>
				<Grid container item direction="row">
					<Grid item xs={12} sm={8}>
						<PageBreadcrumbs nodes={breadcrumbNodes} />
					</Grid>
				</Grid>
				<Grid container>
					<Typography variant="h4">Inventory Updates</Typography>
				</Grid>
				<Grid container>
					<Typography>Upload a CSV file with inventory updates or download the template to get started.</Typography>
				</Grid>
				{batchInfoMessage && (
					<Grid item xs={12}>
						<Alert severity="info">{batchInfoMessage}</Alert>
					</Grid>
				)}
				<Grid item xs={12}>
					<DataGridPro
						autoHeight
						density="standard"
						rows={rows}
						columns={columns}
						paginationMode="client"
						rowsPerPageOptions={[25, 50, 100]}
						pagination
						processRowUpdate={handleRowEdit}
						onProcessRowUpdateError={(error) => console.error(error)}
						components={{
							Toolbar: CustomToolbar,
						}}
						initialState={{
							pagination: {
								pageSize: 25,
							},
							sorting: {
								sortModel: [{ field: "sku", sort: "asc" }],
							},
						}}
					/>
				</Grid>
			</Grid>
			{/* Blocking save modal — same pattern as DisplayRequired (BHS-544). */}
			<LoadingIndicator open={isSaving} content="Saving – please do not close or refresh this page" />
			<Snackbar
				open={!!snackbar?.open}
				autoHideDuration={snackbar?.autoHide ? 5000 : null}
				onClose={(_event, reason) => {
					// Don't auto-close partial-failure outcomes on backdrop click —
					// the user needs to read which batches need to be re-uploaded.
					if (!snackbar?.autoHide && reason === "clickaway") return;
					setSnackbar((prev) => (prev ? { ...prev, open: false } : null));
				}}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
					severity={snackbar?.severity}
					sx={{
						width: "100%",
						bgcolor: snackbar?.severity === "success" ? "success.main" : "error.main",
						color: "#fff",
					}}
				>
					{snackbar?.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default InventoryUpdates;
